import { IpaOptions } from "../../pattern/makeFromPattern";
import { MakeFromPatterns } from "../../pattern/makeFromPatterns";
import { PatternError } from "../../pattern/stringToPattern";
import { ipaToFeatures, IpaType } from "../../phonemes/ipa";
import { segmentPhonemes } from "../../phonemes/segmentPhonemes";
import { orderBySonority } from "../../phonotactics/orderBySonority";
import { getEmptyPhonotacticsInput, PhonotacticsInput, WordGenerator } from "../../phonotactics/wordGenerator";
import { getBasicPhonemesFromSegments } from "../../world/languageQuestions";
import { WordMaker } from "../phonotactics/MakeUpWord";
import { phonotacticsFromString, phonotacticsToString } from "../phonotactics/phonotacticsAsString";

/** local storage key for storing custom phoneme segments */
const makerSegmentsKey = 'maker-segments';
/** local storage key for storing custom phonotactics */
const makerPhonotacticsKey = 'maker-phonotactics';

/** multi-char phonemes that appear on the IPA chart */
const chartMulti = [
  'e̞', 'o̞', 'ẽ', 'i̯', 'u̯',
  'l̩', 'm̩', 'n̩', 'ŋ̩',
  't̪', 'd̪', 'n̪', 'r̪', 'ɾ̪', 'l̪',
  'ts', 'dz', 't̠ʃ', 'd̠ʒ', 'tʃ', 'dʒ', 'ʈʂ', 'ɖʐ', 'tɕ', 'dʑ',
  'pʼ', 't̪ʼ', 'tʼ', 'ʈʼ', 'cʼ', 'kʼ', 'qʼ', 'ɸʼ', 'fʼ', 'θʼ', 'ðʼ', 'wʼ', 'sʼ', 'zʼ', 'ʃʼ', 'ʒʼ', 'ʂʼ', 'çʼ', 'ɕʼ', 'xʼ', 'χʼ', 'ʕʼ', 'hʼ',
];

/**
 * info used for making language sounds, like phoneme segments & phonotactics.
 * words can be generated either from patterns (e.g. CV) or phonotactics (onset, nucleus, coda)
 */
export class MakerState {
  private segments: string[];
  private chartIpa: Set<string> = new Set();
  private diphthongs: string[] = [];
  private patterns?: string;
  private phonotactics?: PhonotacticsInput;
  private wordMaker: WordMaker;
  /** segments sorted by sonority */
  private sonorityList: IpaType[] = [];
  /** phoneme category abbreviations (e.g., V or C) mapped to segments */
  private cats: Record<string, IpaType[]> = {};

  /** use last saved - if no last saved, start with empty */
  constructor() {
    const text1 = window.localStorage.getItem(makerSegmentsKey);
    this.segments = text1 === null ? [] : text1.split(/\s/g).filter(s => s !== '')
    this.extractFromSegments();

    const text2 = window.localStorage.getItem(makerPhonotacticsKey);
    if (text2 !== null && text2.startsWith('syllables:')) {
      // interpret string as phonotactics
      const phonotactics = text2 === null ? getEmptyPhonotacticsInput() : phonotacticsFromString(text2);
      this.phonotactics = { ...phonotactics, catToSegment: this.cats };
      this.wordMaker = new WordGenerator(this.phonotactics);
    } else {
      // interpret string as patterns
      this.patterns = text2 ?? 'CV';
      const maker = new MakeFromPatterns(new IpaOptions(this.segments, this.cats));
      maker.parsePatterns(this.patterns);
      this.wordMaker = maker;
    }
  }

  clear() {
    window.localStorage.removeItem(makerSegmentsKey);
    window.localStorage.removeItem(makerPhonotacticsKey);
    this.segments = [];
    this.chartIpa.clear();
    this.phonotactics = getEmptyPhonotacticsInput();
  }

  getSegments(): string[] { return this.segments; }
  getSegmentsString(): string { return this.segments.join(' '); }

  setSegmentsString(segments: string) {
    this.segments = segments.split(/\s/g).filter(s => s !== '')
    this.extractFromSegments();
    window.localStorage.setItem(makerSegmentsKey, segments);
  }

  /** get all the IPA characters from the segments that appear on an IPA chart */
  getChartIpa(): Set<string> { return this.chartIpa; }
  /** get all the diphthongs from the segments */
  getDiphthongs(): string[] { return this.diphthongs; }

  getPatternsString(): string {
    return this.phonotactics ? phonotacticsToString(this.phonotactics) : this.patterns as string;
  }
  setPatternsString(patterns: string): true | PatternError {
    if (patterns.startsWith('syllables:')) {
      const phonotactics = phonotacticsFromString(patterns);
      this.phonotactics = { ...phonotactics, catToSegment: this.cats };
      this.patterns = undefined;
      this.wordMaker = new WordGenerator(this.phonotactics);
    } else {
      const maker = new MakeFromPatterns(new IpaOptions(this.segments, this.cats));
      const result = maker.parsePatterns(patterns);
      if (result !== true) {
        return result;  // error
      }
      this.patterns = patterns;
      this.phonotactics = undefined;
      this.wordMaker = maker;
    }
    window.localStorage.setItem(makerPhonotacticsKey, patterns);
    return true;
  }

  getPhonotactics(): PhonotacticsInput | undefined { return this.phonotactics; }

  getWordMaker(): WordMaker { return this.wordMaker; }

  /** segments sorted by sonority */
  getSonorityList(): IpaType[] { return this.sonorityList; }
  /** phoneme category abbreviations (e.g., V or C) mapped to segments */
  getCatsToSegments(): Record<string, IpaType[]> { return this.cats; }

  /** use segments to make simple phonotactics: onsets & nuclei */
  segmentsForPhonotactics() {
    const [onsets, nuclei] = getOnsetNucleus(this.segments);
    if (onsets.length > 0 || nuclei.length > 0) {
      const other = this.phonotactics ?? getEmptyPhonotacticsInput();
      this.phonotactics = {
        ...other, // preserve syllable info & cats
        firstOnsets: onsets,
        otherOnsets: onsets,
        nucleus: nuclei,
        lastCodas: [],
        otherCodas: [],
      };

      const asString = phonotacticsToString(this.phonotactics);
      window.localStorage.setItem(makerPhonotacticsKey, asString);
    }
  }
  /** factory settings for simple phonotactics */
  resetPhonotactics() {
    this.phonotactics = {
      ...this.phonotactics, // preserve cats
      syllableCounts: { 2: 3, 3: 2, 1: 1 },
      stressSyllable: 1,
      firstOnsets: ['C'],
      otherOnsets: ['C'],
      nucleus: ['V'],
      lastCodas: [],
      otherCodas: [],
    };

    const asString = phonotacticsToString(this.phonotactics);
    window.localStorage.setItem(makerPhonotacticsKey, asString);
  }

  /** extract derived info from the segments */
  private extractFromSegments() {
    // figure out what chart IPA is represented by the segments
    this.chartIpa.clear();
    const ipa = getBasicPhonemesFromSegments(this.segments, chartMulti);
    ipa.forEach(c => this.chartIpa.add(c));

    // find diphthongs among the segments
    this.diphthongs = [];
    this.segments.forEach(s => {
      const pieces = segmentPhonemes(s, 'ipa');
      const phonemes = pieces.phonemes.map(p => p.phoneme)
      if (phonemes.length > 1) {
        const features = ipaToFeatures(s);
        if (features && features.includes('vowel')) {
          this.diphthongs.push(s);
        }
      }
    });

    // sort by sonority, organize by phoneme category
    const { ordered, leftover, cats } = orderBySonority(this.segments);
    this.cats = cats;
    const lists = ordered.map(c => c.join(' '));
    this.sonorityList = lists.concat(leftover.join(' '));
  }
}

/**
 * separate segments into phonemes that can be used for onsets from nuclei,
 * where vowels & syllabic consonants (e.g., /l̩ m̩ n̩/) can be a nucleus
 */
function getOnsetNucleus(segments: string[]): [string[], string[]] {
  const consonants: string[] = [];
  const vowels: string[] = [];
  for (const segment of segments) {
    const features = ipaToFeatures(segment);
    if (features !== undefined && (features.indexOf('vowel') >= 0 || features.indexOf('syllabic') >= 0)) {
      vowels.push(segment);
    } else if (features !== undefined && features.indexOf('tone') === -1) {
      consonants.push(segment);
    }
  }
  return [consonants, vowels];
}
